import {
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import {
	ANNUAL_VOLUME_DISPLAY,
	DATE_TIME,
	FROM_UTC,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	LOAD_VOLUME_DURATION,
	ProjectDTO,
	RestService,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { UrlService } from '@service/url/url.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { TOOLTIP_MSG } from '@domain/constants/app-variables';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';

@Component({
	selector: 'app-request-for-proposal',
	templateUrl: './request-for-proposal.component.html',
	styleUrls: ['./request-for-proposal.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class RequestForProposalComponent implements OnInit {
	@Input() buttonText = '';
	@Input() projectDTO: ProjectDTO = null;
	@Input() numberOfCarriers: number = 0;
	@Input() isOnRecommendedTab: boolean = false;
	@Input() useMenuButton: boolean = false;
	// CN-8778: the project_carrier_map ids currently shown on the active tab (respecting the
	// filter). Empty = whole project (legacy). Passed through to the send so only these carriers
	// are emailed.
	@Input() carrierMapIds: string[] = [];
	@Output() updatedData = new EventEmitter<string>();

	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;

	constructor(public dialog: MatDialog, public utilService: UtilService) {}

	ngOnInit() {}

	openRFPDialog() {
		const dialogRef = this.dialog.open(RFPDialog, {
			width: '600px',
			data: {
				projectDTO: this.projectDTO,
				numberOfCarriers: this.numberOfCarriers,
				carrierMapIds: this.carrierMapIds,
			},
			disableClose: true,
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result)) {
				this.updatedData.emit(result);
			}
		});
	}

	getTooltipMessage() {
		if (IS_NULL_OR_UNDEFINED(this.numberOfCarriers) || this.numberOfCarriers == 0) {
			return 'Please procure carriers to check capacity for this project';
		}
		if (this.isRFPRequestedToday()) {
			const rfpRequestedAt = DATE_TIME(FROM_UTC(this.projectDTO.rfpRequestedDate));
			return `Capacity can be checked once in a day. Last Capacity Check: ${rfpRequestedAt}`;
		}
		if (this.isOnRecommendedTab) {
			return 'Please switch to Procured tab to Check Capacity';
		}
		return 'Send an email to pick up where you left off with your project carriers. See who has a truck coming up and at what rate.';
	}

	// CN-8778: once-per-day Check Capacity throttle dropped now that the send is scoped to the
	// carriers on a tab/filter (a per-tab/per-filter send shouldn't lock the whole project for the
	// day). Always returns false so the button is never disabled on the daily gate.
	isRFPRequestedToday(): boolean {
		return false;
	}
}

@Component({
	selector: 'app-RFP-dialog',
	templateUrl: 'modal.html',
})
export class RFPDialog implements OnInit {
	isRequestInProgress: boolean = false;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	TOOLTIP_MSG = TOOLTIP_MSG;
	isEditingAnnualVolume: boolean = false;
	ANNUAL_VOLUME_DISPLAY: any = '';
	ANNUAL_VOLUME_TOOLTIP: any = '';
	loadVolumeDuration = [
		LOAD_VOLUME_DURATION.DAY,
		LOAD_VOLUME_DURATION.WEEK,
		LOAD_VOLUME_DURATION.MONTH,
		LOAD_VOLUME_DURATION.YEAR,
	];

	constructor(
		public dialogRef: MatDialogRef<RFPDialog>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		private segmentWrapper: SegmentWrapperService,
		public accountSettings: AccountSettingsService
	) {}

	ngOnInit() {
		this.setAnnualVolumeDisplay();
	}

	sendEmailToCarriers() {
		this.isRequestInProgress = true;
		const url = this.urlService.projectRateRequest(this.data.projectDTO.id);
		const loadVolume = this.data.projectDTO.loadVolume;
		const loadVolumeDuration = this.data.projectDTO.loadVolumeDuration;
		const body = {
			annualVolumeLabel: 'Volume',
			// CN-8799: guard the value — when the project has no volume, `${undefined}/${undefined}`
			// yields the literal "undefined/undefined" in the email. Send '' instead so hermes falls
			// back to its '-' placeholder (it only substitutes when the value is blank).
			annualVolumeValue:
				IS_NOT_NULL_OR_UNDEFINED(loadVolume) && IS_NOT_NULL_OR_UNDEFINED(loadVolumeDuration)
					? `${loadVolume}/${loadVolumeDuration}`
					: '',
			// CN-8778: scope the send to the carriers currently shown on the tab (empty = whole project).
			projectCarrierMapIds: this.data.carrierMapIds || [],
		};
		this.restService.POST(url, body).subscribe(
			(response) => {
				this.utilService.showSnackbar(
					`The selected carriers have been contacted for rates.`,
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				this.isRequestInProgress = false;
				this.closeDialog(true);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to send emails to all carriers now. Please try again later.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.isRequestInProgress = false;
			}
		);
	}

	setAnnualVolumeDisplay() {
		if (
			this.accountSettings.ANNUAL_VOLUME &&
			IS_NOT_NULL_OR_UNDEFINED(this.data.projectDTO.loadVolume) &&
			IS_NOT_NULL_OR_UNDEFINED(this.data.projectDTO.loadVolumeDuration)
		) {
			this.ANNUAL_VOLUME_DISPLAY = ANNUAL_VOLUME_DISPLAY(this.data.projectDTO);
			this.ANNUAL_VOLUME_TOOLTIP = `${this.data.projectDTO.loadVolume}/${this.data.projectDTO.loadVolumeDuration}`;
		}
	}

	getLabelVolumeValue() {
		if (this.accountSettings.ANNUAL_VOLUME) {
			return this.ANNUAL_VOLUME_DISPLAY;
		} else if (
			IS_NOT_NULL_OR_UNDEFINED(this.data.projectDTO.loadVolume) &&
			IS_NOT_NULL_OR_UNDEFINED(this.data.projectDTO.loadVolumeDuration)
		) {
			return `${this.data.projectDTO.loadVolume}/${this.data.projectDTO.loadVolumeDuration}`;
		}
	}

	closeDialog(data: any): void {
		this.dialogRef.close(data);
	}
}
